import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgxSpinnerModule } from 'ngx-spinner';

export interface VersionDiffData {
  originalContent: string;
  versionContent: string;
  originalTitle: string;
  versionTitle: string;
}

interface DiffLine {
  value: string;
  type: 'added' | 'removed' | 'unchanged';
  lineNumber?: number;
}

@Component({
  selector: 'app-version-diff-dialog',
  standalone: true,
  imports: [CommonModule,MatIconModule,MatButtonModule,NgxSpinnerModule,MatFormFieldModule,ReactiveFormsModule,MatProgressSpinnerModule,MatMenuModule,MatDividerModule,MatDialogModule,MatButtonToggleModule,FormsModule,MatSlideToggleModule],
  templateUrl: './version-diff-dialog.component.html',
  styleUrl: './version-diff-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VersionDiffDialogComponent {
  diffLines: DiffLine[] = [];
  viewMode: 'side-by-side' | 'unified' = 'side-by-side';
  showWordDiff = false;

  constructor(
    private dialogRef: MatDialogRef<VersionDiffDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VersionDiffData
  ) {}

  ngOnInit(): void {
    this.generateDiff();
  }

  private generateDiff(): void {
  const originalLines = this.stripHtml(this.data.originalContent).split('\n');
  const versionLines = this.stripHtml(this.data.versionContent).split('\n');

  this.diffLines = this.simpleDiff(versionLines, originalLines);
}

private simpleDiff(oldLines: string[], newLines: string[]): DiffLine[] {
  const result: DiffLine[] = [];
  let lineNumber = 1;

  // Implementación básica - puedes mejorarla según necesites
  const maxLength = Math.max(oldLines.length, newLines.length);

  for (let i = 0; i < maxLength; i++) {
    const oldLine = oldLines[i] || '';
    const newLine = newLines[i] || '';

    if (oldLine === newLine) {
      result.push({
        value: oldLine,
        type: 'unchanged',
        lineNumber: lineNumber++
      });
    } else {
      if (oldLines[i]) {
        result.push({
          value: oldLine,
          type: 'removed'
        });
      }
      if (newLines[i]) {
        result.push({
          value: newLine,
          type: 'added'
        });
      }
    }
  }

  return result;
}



  private stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  }

  onViewModeChange(): void {
    // La vista actual es simple, pero se puede expandir para mostrar side-by-side
    console.log('View mode changed to:', this.viewMode);
  }

  onWordDiffToggle(): void {
    this.generateDiff();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onRestoreVersion(): void {
    this.dialogRef.close('restore');
  }

  getDiffStats(): { added: number; removed: number; unchanged: number } {
    return this.diffLines.reduce((stats, line) => {
      stats[line.type]++;
      return stats;
    }, { added: 0, removed: 0, unchanged: 0 });
  }
}

